import {
  sameBrandReference,
  type DocumentAssetCatalog,
  type DocumentBrandVersion,
} from '../document-assets/document-assets.models';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type {
  NoticeCatalog,
  NoticeComposition,
} from './notice-fidelity.models';
import {
  applyNoticeDocumentSetting,
  noticeDocumentFontChoices,
  noticeDocumentLogoChoices,
  noticeDocumentWatermarkChoices,
  type NoticeDocumentChoice,
  type NoticeDocumentSetting,
} from './notice-document-settings';

type PageTextProperty = 'size' | 'fontCode';
type PageNumberProperty = 'marginPt' | 'fontSizePt' | 'lineHeight';
type BrandingProperty = Exclude<
  Extract<NoticeDocumentSetting, { section: 'branding' }>['property'],
  'profile'
>;

/** Document controls emit intentions against the owner's current source revision. */
@Component({
  selector: 'phms-notice-document-settings',
  standalone: true,
  imports: [FormsModule, UiControlDirective, UiButtonDirective],
  templateUrl: './notice-document-settings.component.html',
  styleUrl: './notice-document-settings.component.scss',
})
export class NoticeDocumentSettingsComponent implements OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input({ required: true }) composition!: NoticeComposition;
  @Input({ required: true }) catalog!: NoticeCatalog;
  @Input() disabled = false;
  @Input() assetCatalog: DocumentAssetCatalog | null = null;
  @Input() assetsLoading = false;
  @Input() assetsError = '';
  @Output() readonly loadAssets = new EventEmitter<void>();
  get selectedBrand(): DocumentBrandVersion | undefined {
    return this.assetCatalog?.brands.find((brand) =>
      sameBrandReference(brand.reference, this.composition.branding.profile),
    );
  }
  get brandKey(): string {
    return this.composition.branding.profile
      ? JSON.stringify(this.composition.branding.profile)
      : '';
  }
  profileKey(brand: DocumentBrandVersion): string {
    return JSON.stringify(brand.reference);
  }
  chooseProfile(value: string): void {
    if (value === '')
      this.change({ section: 'branding', property: 'profile', value: null });
    else {
      const brand = this.assetCatalog?.brands.find(
        (item) => this.profileKey(item) === value,
      );
      if (brand)
        this.change({
          section: 'branding',
          property: 'profile',
          value: brand.reference,
        });
    }
  }
  @Output() readonly setting = new EventEmitter<NoticeDocumentSetting>();
  @Output() readonly breakHistoryGroup = new EventEmitter<void>();
  validationError = '';
  private destroyed = false;

  get fonts(): readonly NoticeDocumentChoice[] {
    return noticeDocumentFontChoices(this.catalog);
  }
  get logos(): readonly NoticeDocumentChoice[] {
    return noticeDocumentLogoChoices(this.catalog);
  }
  get watermarks(): readonly NoticeDocumentChoice[] {
    return noticeDocumentWatermarkChoices(this.catalog);
  }
  get knownSize(): boolean {
    return ['letter', 'a4'].includes(this.composition.page.size);
  }
  get knownFont(): boolean {
    return this.fonts.some(
      (choice) => choice.code === this.composition.page.fontCode,
    );
  }
  get knownLogo(): boolean {
    return this.logos.some(
      (choice) => choice.code === this.composition.branding.logoCode,
    );
  }
  get knownWatermark(): boolean {
    return this.watermarks.some(
      (choice) => choice.code === this.composition.branding.watermarkCode,
    );
  }
  get knownAccent(): boolean {
    return /^#[a-f0-9]{6}$/i.test(this.composition.branding.accentColor);
  }

  pageText(property: PageTextProperty, value: string): void {
    this.change({ section: 'page', property, value });
  }
  pageNumber(property: PageNumberProperty, value: number): void {
    this.change({ section: 'page', property, value });
  }
  branding(property: BrandingProperty, value: string): void {
    this.change({ section: 'branding', property, value });
  }
  blur(): void {
    if (!this.disabled && !this.destroyed) this.breakHistoryGroup.emit();
  }

  /** Called after the owning Page inspector has rendered. */
  focus(property: NoticeDocumentSetting['property']): boolean {
    if (this.disabled || this.destroyed) return false;
    const control = Array.from(
      this.host.nativeElement.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >('[data-document-setting]'),
    ).find((element) => element.dataset['documentSetting'] === property);
    if (!control || control.disabled) return false;
    control.focus();
    return this.host.nativeElement.ownerDocument.activeElement === control;
  }

  ngOnChanges(): void {
    this.validationError = '';
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.validationError = '';
  }

  private change(setting: NoticeDocumentSetting): void {
    if (this.disabled || this.destroyed) return;
    const current =
      setting.section === 'page'
        ? this.composition.page[setting.property]
        : this.composition.branding[setting.property];
    if (setting.value === current) {
      this.validationError = '';
      return;
    }
    // Preflight only: the source owner reapplies this intention to its current
    // revision. Never retain or publish a separately owned document here.
    const result = applyNoticeDocumentSetting(
      this.composition,
      this.catalog,
      setting,
      this.assetCatalog,
    );
    if (!result.ok) {
      this.validationError = `${result.message} Change not applied.`;
      return;
    }
    this.validationError = '';
    this.setting.emit(setting);
  }
}
